import { useSelector } from 'react-redux';
import { selectDoesUserHaveRbacPermission } from 'core/userContext/selectors';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { MEDIA_LAYOUT_ICONS, PLACEMENT_DISPLAY_CONTENT } from 'modules/placements/constants';
import { OpenNewWindow16 } from 'ui/icons';
import { BaseOption } from 'ui/selects';
import { Placement } from 'modules/placements/types';
import { PlacementOptionIcon, PlacementOptionLabel } from '../styles';
import { PlacementWrapper, RightAlignedHelpText, StyledFormField, StyledSelect } from './styles';

type SelectPlacementFormFieldProps = {
  placements: Placement[];
  selectPlacement: (placementId: number) => void;
  errorMessage?: React.ReactNode;
};

const { formFieldPlaceholder, formFieldTitleLabel, managePlacementsLabel, managePlacementsPath } =
  PLACEMENT_DISPLAY_CONTENT;

export const SelectPlacementFormField = ({
  placements,
  selectPlacement,
  errorMessage,
}: SelectPlacementFormFieldProps) => {
  const canViewPlacements = useSelector(selectDoesUserHaveRbacPermission)(
    PROJECT_PERMISSIONS.ViewChannelConfiguration,
  );

  const placementSelectOptions: BaseOption<number>[] = placements.map((placement) => ({
    id: placement.id.toString(),
    label: (
      /** @note Adding icon to the label instead of using the Prefix property to display icon in the Trigger */
      <PlacementOptionLabel>
        <PlacementOptionIcon>{MEDIA_LAYOUT_ICONS[placement.mediaLayout]}</PlacementOptionIcon>
        {placement.placementName}
      </PlacementOptionLabel>
    ),
    value: placement.id,
  }));

  const ManagePlacementsLink = canViewPlacements ? (
    <RightAlignedHelpText
      data-test="manage-placements-link"
      onClick={() => window.open(managePlacementsPath, '_blank')}
    >
      <span>{managePlacementsLabel}</span>
      <OpenNewWindow16 width={14} isColorInherited />
    </RightAlignedHelpText>
  ) : null;

  return (
    <PlacementWrapper>
      <StyledFormField
        data-test="form-field-placement"
        labelText={formFieldTitleLabel}
        errorText={errorMessage}
        hasError={!!errorMessage}
        isRequired
      >
        <StyledSelect
          id="form-field-placement-select"
          data-test="form-field-placement-select"
          onSelect={([placementId]) => selectPlacement(Number(placementId))}
          options={placementSelectOptions}
          placeholder={formFieldPlaceholder}
          isFullWidth
          isError={!!errorMessage}
        />
      </StyledFormField>
      {ManagePlacementsLink}
    </PlacementWrapper>
  );
};
